import { showMessageNotification } from '@app/lib'
import { AspectRatio, Box, Stack, Text, useMantineTheme } from '@mantine/core'
import { Dropzone, type FileWithPath } from '@mantine/dropzone'
import { IconPhotoPlus, IconUpload, IconX } from '@tabler/icons-react'
import { type FC, type RefObject } from 'react'

const ACCEPT = ['image/jpeg', 'image/png']
const MAX_SIZE = 5_242_880 // 5 MB inclusive

export interface AddPhotoTileProps {
  disabled?: boolean
  onAdd: (file: File) => void
  openRef?: RefObject<() => void | undefined>
}

export const AddPhotoTile: FC<AddPhotoTileProps> = ({ disabled = false, onAdd, openRef }) => {
  const theme = useMantineTheme()

  return (
    <AspectRatio ratio={1}>
      <Dropzone
        accept={ACCEPT}
        aria-label='Add photo'
        disabled={disabled}
        maxSize={MAX_SIZE}
        multiple={false}
        onDrop={(dropped: FileWithPath[]) => {
          const file = dropped[0]
          if (file) onAdd(file)
        }}
        onReject={(fileRejections) => {
          const code = fileRejections[0]?.errors[0]?.code
          if (code === 'file-too-large') {
            showMessageNotification({ message: 'Photo exceeds the 5 MB limit.', type: 'warning' })
          } else if (code === 'file-invalid-type') {
            showMessageNotification({ message: 'Photo must be JPEG or PNG.', type: 'warning' })
          } else {
            showMessageNotification({ message: 'That file could not be accepted.', type: 'warning' })
          }
        }}
        openRef={openRef}
        radius='md'
        style={{ height: '100%' }}
      >
        <Box style={{ alignItems: 'center', display: 'flex', height: '100%', justifyContent: 'center', pointerEvents: 'none' }}>
          <Stack align='center' spacing={4}>
            <Dropzone.Accept>
              <IconUpload color={theme.colors[theme.primaryColor][6]} size='2rem' stroke={1.5} />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX color={theme.colors.red[6]} size='2rem' stroke={1.5} />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconPhotoPlus size='2rem' stroke={1.5} />
            </Dropzone.Idle>
            <Text size='xs' ta='center'>Click or drag a JPEG / PNG (max 5 MB)</Text>
          </Stack>
        </Box>
      </Dropzone>
    </AspectRatio>
  )
}
